import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Checkbox,
  TableCell,
  TableRow,
  Typography,
} from '@material-ui/core';
import { singleUnitName } from 'helpers/other';
import { TextFormattedNumber } from 'components';
import { imgBaseUrl } from 'configs';
import round from 'helpers/mathRound';
import { getDiscount } from '../utils/getDiscount';
import { extractAttributesValues } from '../utils/extractAttributesValues';
import { getAlleDiscount } from '../helpers';
import { useStyles } from '../styles';
import logo from '../../../components/Logo/logo-dermpro.svg';


const attributesToFind = ['short_description', 'additional_subscription_price', 'quotepro_discounts'];

const QuoteSummaryTableRow = ({
  item,
  selectedItems,
  handleItemSelect,
}) => {
  const classes = useStyles();
  const [loaded, setLoaded] = useState(false);

  const {
    customerPushCart,
    quoteProducts
  } = useSelector((state: IReduxState) => state.quote);
  const { stores = [] } = useSelector((state: IReduxState) => state.user.profile);
  const mediaStoreUrl = `${stores[0]?.apiUrl}/media/catalog/product` || imgBaseUrl;

  const {
    sku,
    qty,
    product_option: {
      extension_attributes: {
        subscription_id,
        quotepro_discounts: customQuoteproDiscounts
      },
    },
    price: updatedPrice,
    custom_subscription_price
  } = item;

  const {
    image,
    subscription_options,
    subscription_active,
    catalog_product_unit,
    savings,
    subscription_label,
    additional_subscription_price,
    quotepro_discounts
  } = customerPushCart.products[sku]?.product || {};

  const quoteProductData = quoteProducts?.find((quoteItem) => quoteItem.sku === sku) as IQuoteProduct || {};
  const { name, price, custom_attributes } = quoteProductData as IQuoteProduct || {};

  const itemPrice = updatedPrice || price;

  const attributesValues = custom_attributes
    ? extractAttributesValues(custom_attributes, attributesToFind)
    : [];


  const selectedItemsWithProductInfo = Object.values(customerPushCart?.products || {}).some((item) => {
    const product = item as { product?: { additional_subscription_price?: string } };
    return product.product?.additional_subscription_price;
  });

  const savingsValue = round(Number(savings)) || 0;

  const dataForDiscount = {
    savings: savingsValue,
    discount_options: quotepro_discounts && quotepro_discounts !== '[]' ? JSON.parse(quotepro_discounts) : [customQuoteproDiscounts],
    itemPrice
  };

  const isAlleSKU = item.sku.includes('bdsku');

  const discount = isAlleSKU
    ? round(getAlleDiscount({ sku, name })) || 0
    : getDiscount(item, dataForDiscount) || 0;

  const unitsName = catalog_product_unit || 'units';
  const subscriptionData = subscription_options?.find((s) => s.id === subscription_id);
  const isNullPrice = subscriptionData?.installment_price === null && subscriptionData?.adjustment_price === null;

  const regularPrice = !!subscriptionData && attributesValues.subscription_active === '1' && !isNullPrice && updatedPrice === 0
    ? round(subscriptionData.installment_price + subscriptionData.adjustment_price)
    : itemPrice || 0;

  const subtotal = regularPrice;

  const amount = savingsValue && !updatedPrice
    ? (regularPrice * qty)
    : (regularPrice * qty) - discount;

  useEffect(() => {
    setLoaded(false);
  }, [image]);

  return (
    <TableRow key={item._id}>
      <TableCell padding="checkbox">
        <Checkbox
          checked={selectedItems.includes(item._id)}
          color="primary"
          className="mt-2"
          onChange={(event) => handleItemSelect(item._id, event.target.checked)}
        />
      </TableCell>
      <TableCell width="70px">
        <img
          src={loaded ? `${mediaStoreUrl}${image}` : logo}
          alt={`${name} image`}
          className={classes.quoteSummaryTableImage}
          onLoad={() => setLoaded(true)}
          onError={(e) => {
            (e.target as HTMLImageElement).onerror = null;
            (e.target as HTMLImageElement).src = logo;
          }}
        />
      </TableCell>
      <TableCell width="30%">
        <Typography variant="subtitle2" className="font-semibold" gutterBottom>
          {name}&nbsp;
          <span className="text-13 font-italic font-medium whitespace-no-wrap">
            (<TextFormattedNumber value={round(updatedPrice ? updatedPrice : itemPrice - savingsValue)} prefix="$" />/
            {singleUnitName(unitsName)})
          </span>
        </Typography>
        {subscription_active === '1' && !!subscriptionData?.frequency_count &&
        <>
          <div className="flex flex-wrap">
            <span className="text-13 whitespace-no-wrap mr-1 font-italic font-bold">
              {subscription_label}{':'}
            </span>
            <span className="text-13 whitespace-no-wrap font-italic">
              {subscriptionData?.sproduct_label || ''}
            </span>
          </div>
          <div className="flex flex-wrap mb-2">
            <span className="text-13 whitespace-no-wrap mr-1 font-italic font-bold">
              {'Periodic Price:'}
            </span>
            <span className="text-13 whitespace-no-wrap font-italic">
              <TextFormattedNumber
                value={custom_subscription_price
                  ? custom_subscription_price
                  : round(subscriptionData?.installment_price) || 0
                }
                prefix="$"
              />
            </span>
          </div>
        </>
        }
        <Typography dangerouslySetInnerHTML={{ __html: attributesValues?.short_description || '' }} />
      </TableCell>
      <TableCell>
        <Typography variant="subtitle2" className="font-semibold" gutterBottom>Quantity</Typography>
        <Typography variant="body2">{`${qty} ${
          qty === 1 ? singleUnitName(unitsName) : unitsName
        }`}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="subtitle2" className="font-semibold" gutterBottom>Subtotal</Typography>
        <TextFormattedNumber value={subtotal * qty} prefix="$" />
      </TableCell>
      <TableCell>
        <Typography variant="subtitle2" className="font-semibold" gutterBottom>Savings</Typography>
        <TextFormattedNumber value={discount} prefix="$" />
      </TableCell>
      {selectedItemsWithProductInfo &&
      <TableCell>
        {additional_subscription_price &&
          <>
            <Typography variant="subtitle2" className="font-semibold" gutterBottom>Monthly Fee</Typography>
            <TextFormattedNumber value={additional_subscription_price} prefix="$" />
          </>
        }
      </TableCell>}
      <TableCell className="font-semibold">
        <Typography variant="subtitle2" className="font-semibold" gutterBottom>TOTAL</Typography>
        <TextFormattedNumber value={isAlleSKU ? 0 : amount} prefix="$" />
      </TableCell>
    </TableRow>
  );
};

export default QuoteSummaryTableRow;
